import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PersonsPage = () => {
  const [persons, setPersons] = useState([]);
  const [filteredPersons, setFilteredPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Pré-remplir la recherche depuis l'URL (?search=Dupont)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('search');
    if (s) setSearchTerm(s);
  }, [location.search]);

  useEffect(() => {
    fetchPersons();
  }, []);

  useEffect(() => {
    let filtered = persons;

    if (selectedLetter) {
      filtered = filtered.filter(p => (p.given_name || '').trim().charAt(0).toUpperCase() === selectedLetter);
    }

    if (searchTerm) {
      filtered = filtered.filter(p => {
        const fullName = `${p.given_name || ''} ${p.surname || ''}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
      });
    }

    setFilteredPersons(filtered);
  }, [searchTerm, selectedLetter, persons]);

  const fetchPersons = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/persons`, { withCredentials: true });
      setPersons(data);
      setFilteredPersons(data);
    } catch (error) {
      console.error('Error fetching persons:', error);
    } finally {
      setLoading(false);
    }
  };

  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const availableLetters = new Set(
    persons
      .map(p => (p.given_name || '').trim().charAt(0).toUpperCase())
      .filter(Boolean)
  );

  return (
    <div data-testid="persons-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-forest mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Personnes
        </h1>
        <p className="text-stone-600">Liste des individus de votre arbre généalogique</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={20} />
        <Input
          data-testid="persons-search-input"
          type="text"
          placeholder="Rechercher une personne..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 border-parchment-dark focus:ring-forest"
        />
      </div>

      <div data-testid="persons-alphabet-filter" className="flex flex-wrap gap-1">
        <button
          type="button"
          data-testid="alphabet-letter-all"
          onClick={() => setSelectedLetter('')}
          className={`px-2.5 py-1 text-sm border transition-colors ${
            selectedLetter === ''
              ? 'bg-forest text-white border-forest'
              : 'bg-white text-stone-600 border-parchment-dark hover:bg-stone-100'
          }`}
        >
          Tous
        </button>
        {ALPHABET.map((letter) => {
          const isAvailable = availableLetters.has(letter);
          const isActive = selectedLetter === letter;
          return (
            <button
              key={letter}
              type="button"
              data-testid={`alphabet-letter-${letter}`}
              onClick={() => isAvailable && setSelectedLetter(isActive ? '' : letter)}
              disabled={!isAvailable}
              className={`w-8 h-8 text-sm border transition-colors ${
                isActive
                  ? 'bg-forest text-white border-forest'
                  : isAvailable
                  ? 'bg-white text-stone-600 border-parchment-dark hover:bg-stone-100'
                  : 'bg-stone-50 text-stone-300 border-parchment-dark cursor-not-allowed'
              }`}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div data-testid="persons-loading" className="text-center py-12 text-stone-500">Chargement...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPersons.map((person) => (
            <div
              key={person.handle}
              data-testid={`person-card-${person.handle}`}
              onClick={() => navigate(`/persons/${person.handle}`)}
              className="bg-white border border-parchment-dark p-6 hover:-translate-y-1 transition-transform cursor-pointer"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-stone-200 border border-parchment-dark flex items-center justify-center text-forest font-bold text-lg">
                  {person.given_name?.charAt(0) || '?'}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-forest" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {person.given_name || ''} {person.surname || ''}
                  </h3>
                  {person.gramps_id && (
                    <p className="text-sm text-stone-500">ID: {person.gramps_id}</p>
                  )}
                  {person.gender !== undefined && person.gender !== null && (
                    <p className="text-sm text-stone-600 mt-1">
                      {{ 0: 'Féminin', 1: 'Masculin', 2: 'Inconnu', 3: 'Autre' }[Number(person.gender)] ?? ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredPersons.length === 0 && (
        <div data-testid="persons-empty" className="text-center py-12 text-stone-500">
          Aucune personne trouvée
        </div>
      )}
    </div>
  );
};